import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";

import { api } from "../api.js";
import ThalamosLogo from "../assets/img/thalamos_logo.svg";
import { config } from "../config.js";
import { Button } from "~/components/design-system/index.js";

const COUNTDOWN_SECONDS = 5;

type State =
  | { type: "loading" }
  | {
      // A remembered identity was found — confirm before redirecting.
      type: "confirm";
      displayName: string;
      maskedEmail: string;
      loginUrl: string;
      cancelUrl: string;
    }
  | {
      // Flag on but no mapping yet — offer to remember on this device.
      type: "optIn";
      instanceDisplayName: string;
    };

/**
 * Centred card matching the login page (LoginPage.tsx) — same Thalamos logo,
 * width, shadow and copy register, since this interstitial is the pre-login
 * gateway into eMHA (the clinician is arriving from their EPR).
 */
function InterstitialShell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "500px",
          padding: "2em 2em 3em",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          "@media (max-width: 600px)": {
            padding: "1em 1em 2em",
          },
        }}
      >
        <img
          src={ThalamosLogo}
          alt="Thalamos Logo"
          style={{ width: "60%", height: "auto", marginBottom: "1.5em" }}
        />
        {children}
      </Box>
    </Box>
  );
}

/**
 * Confirmation view shown when this device has a remembered sign-in: the masked
 * identity plus a countdown that auto-proceeds, with manual continue/cancel.
 */
export function ConfirmView({
  displayName,
  maskedEmail,
  secondsLeft,
  onContinue,
  onCancel,
}: {
  displayName: string;
  maskedEmail: string;
  secondsLeft: number;
  onContinue: () => void;
  onCancel: () => void;
}) {
  return (
    <InterstitialShell>
      <Typography sx={{ fontSize: "1.2rem", color: "#666" }}>
        Logging you in to eMHA as
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, marginTop: "0.25em" }}>
        {displayName}
      </Typography>
      <Typography sx={{ color: "#666" }}>{maskedEmail}</Typography>
      <Typography sx={{ color: "#666", marginBottom: "1.5em" }}>
        Continuing in {secondsLeft}s…
      </Typography>
      <Stack direction="row" sx={{ gap: "0.5rem", justifyContent: "center" }}>
        <Button
          label="Continue now"
          testId="clickthrough-continue"
          onClick={onContinue}
        />
        <Button
          label="Not you? Cancel"
          variant="outlined"
          testId="clickthrough-cancel"
          onClick={onCancel}
        />
      </Stack>
    </InterstitialShell>
  );
}

/**
 * Opt-in view shown when the instance allows remembering a sign-in but none is
 * bound to this device yet — offers to remember before continuing to login.
 */
export function OptInView({
  instanceDisplayName,
  rememberMe,
  onToggleRememberMe,
  onContinue,
}: {
  instanceDisplayName: string;
  rememberMe: boolean;
  onToggleRememberMe: () => void;
  onContinue: () => void;
}) {
  return (
    <InterstitialShell>
      <Typography
        sx={{ fontSize: "1.2rem", color: "#666", marginBottom: "1em" }}
      >
        {"Click 'Log in' to access eMHA"}
      </Typography>
      <FormControlLabel
        sx={{ justifyContent: "center", marginBottom: "1em" }}
        control={
          <Checkbox
            checked={rememberMe}
            onChange={onToggleRememberMe}
            data-testid="clickthrough-remember-me"
          />
        }
        label={`Remember my sign-in on this device for ${instanceDisplayName}`}
      />
      <Box>
        <Button
          label="Log in"
          testId="clickthrough-optin-continue"
          onClick={onContinue}
        />
      </Box>
    </InterstitialShell>
  );
}

/**
 * Pre-login interstitial for the clickthrough remembered-login feature. It peeks
 * the one-time token (without consuming it) and then either:
 *
 * - shows "Logging you in to eMHA as …" + a short countdown when this device
 *   already has a remembered sign-in (redirecting to Auth0 with a login_hint), or
 * - offers a "remember me on this device" opt-in when the instance has the flag
 *   on but no mapping exists yet (the choice rides through login in returnTo and
 *   is bound server-side at token redeem).
 *
 * Anything else (missing token, peek failure, flag off) falls back to a normal
 * login that still completes the clickthrough.
 */
export function ClickThroughLaunchPage() {
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = qs.get("token");
  const source = qs.get("source");

  const [state, setState] = useState<State>({ type: "loading" });
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [rememberMe, setRememberMe] = useState(false);

  // A true normal login with no clickthrough returnTo — used when we have no
  // usable token to complete the launch with.
  const normalLoginUrl = `${config.backendUrl}/api/oauth/login`;

  // Builds the backend login URL that lands the clinician back on the
  // team-context selector -> patient context launch.
  const buildLoginUrl = useCallback(
    (opts?: { loginHint?: string; rememberLogin?: boolean }) => {
      const innerParams = new URLSearchParams();
      innerParams.set("token", token ?? "");
      innerParams.set("source", source ?? "");
      if (opts?.rememberLogin) {
        innerParams.set("rememberLogin", "true");
      }
      const returnTo = `/context-selector?returnTo=${encodeURIComponent(
        `/patients/context-launch?${innerParams.toString()}`,
      )}`;
      const params = new URLSearchParams();
      params.set("returnTo", returnTo);
      if (opts?.loginHint) {
        params.set("login_hint", opts.loginHint);
      }
      return `${config.backendUrl}/api/oauth/login?${params.toString()}`;
    },
    [token, source],
  );

  useEffect(() => {
    const run = async () => {
      // Without a token/source there's nothing to complete — do a true normal
      // login rather than a broken returnTo pointing at an empty token.
      if (!token || !source) {
        window.location.href = normalLoginUrl;
        return;
      }

      try {
        const response = await api.eprClickThrough.peek(source, token);
        if (response.status === 400) {
          // Invalid/expired token — the clickthrough can't complete, so a
          // returnTo here would only fail at redeem after login. Send the user
          // to a plain login instead.
          window.location.href = normalLoginUrl;
          return;
        }
        if (response.status !== 200) {
          // Transient (5xx): keep the clickthrough returnTo so it can still
          // complete once login succeeds.
          window.location.href = buildLoginUrl();
          return;
        }

        const { remembered, rememberLoginEnabled, instanceDisplayName } =
          response.data;

        if (remembered) {
          setState({
            type: "confirm",
            displayName: remembered.displayName,
            maskedEmail: remembered.maskedEmail,
            loginUrl: buildLoginUrl({ loginHint: remembered.email }),
            cancelUrl: buildLoginUrl(),
          });
          return;
        }

        if (rememberLoginEnabled) {
          setState({ type: "optIn", instanceDisplayName });
          return;
        }

        // Flag off (shouldn't reach here) — proceed with a normal login.
        window.location.href = buildLoginUrl();
      } catch {
        // Peek failed (network/5xx): don't strand the user on a spinner — fall
        // back to a login that still completes the clickthrough (no hint).
        window.location.href = buildLoginUrl();
      }
    };

    void run();
  }, [token, source, buildLoginUrl, normalLoginUrl]);

  // Countdown + auto-redirect once we're showing the confirmation.
  useEffect(() => {
    if (state.type !== "confirm") {
      return;
    }
    if (secondsLeft <= 0) {
      window.location.href = state.loginUrl;
      return;
    }
    const timer = setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [state, secondsLeft]);

  if (state.type === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state.type === "optIn") {
    return (
      <OptInView
        instanceDisplayName={state.instanceDisplayName}
        rememberMe={rememberMe}
        onToggleRememberMe={() => {
          setRememberMe((v) => !v);
        }}
        onContinue={() => {
          window.location.href = buildLoginUrl({ rememberLogin: rememberMe });
        }}
      />
    );
  }

  return (
    <ConfirmView
      displayName={state.displayName}
      maskedEmail={state.maskedEmail}
      secondsLeft={secondsLeft}
      onContinue={() => {
        window.location.href = state.loginUrl;
      }}
      onCancel={() => {
        window.location.href = state.cancelUrl;
      }}
    />
  );
}
